/**
 * Préparer et envoyer une demande de soumission — chunk 9.AZ.1.
 *
 * ## Ce que cet écran répare
 *
 * Le courtier pilote a écrit : « je ne peux toujours pas monter de vraie
 * soumission », « pouvoir sélectionner Sunlife pour moi n'est pas une soumission,
 * je dois lui dire ce que je veux ». Les briques existaient toutes — cahier de
 * charges structuré, artefact PDF, envois individuels, suivi par assureur — mais
 * aucune page ne les mettait dans l'ordre où il travaille.
 *
 * ## Le vocabulaire est le sien
 *
 * L'écran dit **cahier de charges**, **demande de soumission**, **assureurs**,
 * **envoi**. Il ne dit jamais `form_response`, `template version` ni
 * `quote_request`. Le versionnement reste la puissance du système ; il n'a pas à
 * devenir la charge mentale du conseiller.
 *
 * ## Ce que la page NE fait pas
 *
 * Aucune règle métier n'est rejouée ici. Le serveur décide si une réponse est
 * rattachable, si un document est périmé, si une demande peut partir. La page
 * lit ses refus et les rend lisibles — elle ne les anticipe pas, et surtout elle
 * ne les contredit pas.
 */

import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { PREPARATION_SECTIONS } from "../../lib/preparationNavigation";
import { useFileDownloadUrl } from "../../lib/queries/files";
import { useFormResponses } from "../../lib/queries/formResponses";
import { useProject } from "../../lib/queries/projects";
import {
  useAddQuoteRequestRecipient,
  useAttachQuoteRequestSpec,
  useQuoteRequest,
  useQuoteRequestSpecs,
  useRegenerateQuoteRequestSpec,
  useRemoveQuoteRequestRecipient,
  useSendQuoteRequest,
  useGenererExportEffectif,
  useSendReadiness,
  useUpdateQuoteRequest,
  useUpdateQuoteRequestRecipient,
} from "../../lib/queries/quoteRequests";
import { scrollWorkspaceTarget } from "../../lib/scrollWorkspaceTarget";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";
import { CensusSection } from "../projects/_parts/CensusSection";

import { AuthorizationStep } from "./_parts/AuthorizationStep";
import { DeliveryPreparation } from "./_parts/DeliveryPreparation";
import { EnvoiRecapitulatif } from "./_parts/EnvoiRecapitulatif";
import { EtapeAssureurs } from "./_parts/EtapeAssureurs";
import { EtapeCahierDeCharges } from "./_parts/EtapeCahierDeCharges";
import { EtapeEffectifAssureur } from "./_parts/EtapeEffectifAssureur";
import { SendBlockers } from "./_parts/SendBlockers";
import { SuiviParAssureur } from "./_parts/SuiviParAssureur";

export function PrepareSendPage() {
  const { id } = useParams<{ id: string }>();
  return <PrepareSendWorkspace key={id} id={id} />;
}

export function PrepareSendWorkspace({
  id,
  presentation = "page",
}: {
  id: string | undefined;
  presentation?: "page" | "dossier";
}) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { hash } = useLocation();
  const taskStart = useRef<HTMLDivElement>(null);
  const demande = useQuoteRequest(id);
  // 9.BJ.1 — le verdict d'envoi vient du serveur, jamais d'un recalcul local.
  const readiness = useSendReadiness(id);
  const genererExport = useGenererExportEffectif(id);

  /**
   * Produire l'effectif transmis — geste explicite, jamais automatique.
   *
   * Le refus est montré tel que le serveur le formule : l'écran ne traduit pas
   * un code en phrase de son cru.
   */
  const produireExportEffectif = async () => {
    try {
      const r = await genererExport.mutateAsync();
      toast.success(t("census_export.done", { count: r.lignes }));
    } catch (e) {
      toast.error(
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : t("errors.generic"),
      );
    }
  };
  const cahiers = useQuoteRequestSpecs(id);
  const rattacher = useAttachQuoteRequestSpec();
  const refaire = useRegenerateQuoteRequestSpec();
  const envoyer = useSendQuoteRequest();
  const telecharger = useFileDownloadUrl();

  /** La section appartient à la navigation ; les états métier viennent du serveur. */
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = presentation === "dossier" ? "request_section" : "section";
  const selectedStep = searchParams.get(sectionParam);
  function setSelectedStep(step: string | null | undefined) {
    // A legacy anchor can select the only request without naming it in the URL.
    // Naming that same verified request is navigation within the current form.
    if (presentation === "dossier" && id && !searchParams.get("request")) {
      allowNavigation();
    }
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (presentation === "dossier" && id) {
        next.set("view", "preparation");
        next.set("request", id);
      }
      if (step) next.set(sectionParam, step);
      else next.delete(sectionParam);
      return next;
    });
    requestAnimationFrame(() =>
      taskStart.current ? scrollWorkspaceTarget(taskStart.current) : undefined,
    );
  }
  const [aRefaire, setARefaire] = useState<string | null>(null);
  /** Un envoi en vol. Bloque le second clic sans dépendre de la latence réseau. */
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [sentRequestId, setSentRequestId] = useState<string | null>(null);
  const [deliveryDirty, setDeliveryDirty] = useState(false);
  const [echeance, setEcheance] = useState<string | null>(null);
  const [savedEcheance, setSavedEcheance] = useState<string | null>(null);

  const projectId = demande.data?.project_id;
  const project = useProject(projectId);
  // Les analyses DÉJÀ soumises du dossier : ce sont les seules rattachables, et
  // le serveur refuse les brouillons. On ne propose donc que ce qui peut marcher.
  const reponses = useFormResponses(
    projectId ? { project_id: projectId, status: "submitted" } : {},
  );

  // 9.BC.1 — l'étape 2 décide, elle ne constate plus. Les trois gestes passent
  // par les endpoints existants ; l'adresse est résolue par le serveur.
  const ajouterAssureur = useAddQuoteRequestRecipient();
  const retirerAssureur = useRemoveQuoteRequestRecipient();
  const corrigerAdresse = useUpdateQuoteRequestRecipient();
  const enregistrerDemande = useUpdateQuoteRequest();
  const assureursEnCours =
    ajouterAssureur.isPending || retirerAssureur.isPending || corrigerAdresse.isPending;

  const cahier = cahiers.data?.items?.[0] ?? null;
  const destinataires = useMemo(() => demande.data?.recipients ?? [], [demande.data]);
  const estBrouillon = demande.data?.status === "draft";

  const echeanceEffective = echeance ?? (demande.data?.expected_response_by ?? "").slice(0, 10);
  const modificationsNonEnvoyees =
    estBrouillon &&
    echeance !== null &&
    echeance !== (savedEcheance ?? (demande.data?.expected_response_by ?? "").slice(0, 10));
  const { allowNavigation } = useUnsavedChangesGuard(
    deliveryDirty || modificationsNonEnvoyees,
    t("preparation_context.unsaved"),
    presentation === "dossier" ? "request" : undefined,
  );

  // ---------------------------------------------------------------------------
  // Gestes
  // ---------------------------------------------------------------------------

  async function choisirCahier(formResponseId: string) {
    if (!id) return;
    try {
      await rattacher.mutateAsync({ quoteRequestId: id, formResponseId });
      setARefaire(null);
      toast.success(t("prepare_send.spec.attached"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  async function ajouterUnAssureur(insurerCode: string) {
    if (!id) return;
    try {
      await ajouterAssureur.mutateAsync({
        quoteRequestId: id,
        input: { insurer_code: insurerCode },
      });
      toast.success(t("prepare_send.insurers.added"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  async function retirerUnAssureur(recipientId: string) {
    if (!id) return;
    try {
      await retirerAssureur.mutateAsync({ quoteRequestId: id, recipientId });
      toast.success(t("prepare_send.insurers.removed"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  /**
   * Une adresse pour CET envoi — le référentiel n'est pas touché.
   *
   * Écrire l'exception dans le registre en ferait le défaut de tous les envois
   * suivants, sans que personne ne l'ait demandé.
   */
  async function corrigerUneAdresse(recipientId: string, email: string) {
    if (!id) return;
    try {
      await corrigerAdresse.mutateAsync({
        quoteRequestId: id,
        recipientId,
        input: { recipient_email: email },
      });
      toast.success(t("prepare_send.insurers.address_saved"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  /**
   * 9.BD.1 — la date de réponse attendue rejoint l'état SERVEUR.
   *
   * Elle ne vivait que dans un `useState` et n'était transmise qu'au `/send` :
   * une navigation interne la perdait en silence, et « reprendre une demande »
   * rendait tout sauf elle. Le `PATCH` existe déjà — il ne manquait que l'appel.
   *
   * Sauvegarde explicite ; l’échec reste visible et conserve la saisie.
   * Une saisie plus récente pendant la requête n’est jamais effacée.
   */
  async function enregistrerEcheance(valeur: string) {
    if (!id || !estBrouillon || enregistrerDemande.isPending) return;
    const deja = savedEcheance ?? (demande.data?.expected_response_by ?? "").slice(0, 10);
    if (valeur === deja) return;
    try {
      await enregistrerDemande.mutateAsync({
        quoteRequestId: id,
        input: {
          expected_response_by: valeur ? new Date(valeur).toISOString() : null,
        },
      });
      setSavedEcheance(valeur);
      toast.success(t("preparation_context.saved"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  async function changerLangueDestinataire(recipientId: string, language: "fr" | "en") {
    if (!id || !estBrouillon || assureursEnCours) return;
    try {
      await corrigerAdresse.mutateAsync({ quoteRequestId: id, recipientId, input: { language } });
      toast.success(t("prepare_send.insurers.language_saved"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  async function refaireDocument() {
    if (!id || !cahier) return;
    try {
      await refaire.mutateAsync({ quoteRequestId: id, specId: cahier.id });
      setARefaire(null);
      toast.success(t("prepare_send.spec.regenerated"));
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  async function envoyerLaDemande() {
    // La garde qui PORTE réellement le non-doublon est le `disabled` du bouton :
    // React repeint entre deux clics, et le second tombe sur un bouton inerte.
    // Celle-ci ne protège que d'un appel programmatique — aucun sabotage ne peut
    // donc la faire tomber, et c'est dit plutôt que laissé croire couvert.
    if (
      !id ||
      envoiEnCours ||
      deliveryDirty ||
      modificationsNonEnvoyees ||
      enregistrerDemande.isPending
    )
      return;
    setEnvoiEnCours(true);
    try {
      await envoyer.mutateAsync({
        quoteRequestId: id,
        ...(echeance ? { input: { expected_response_by: new Date(echeance).toISOString() } } : {}),
        ...(projectId ? { projectId } : {}),
      });
      setEcheance(null);
      setSentRequestId(id);
      toast.success(t("prepare_send.send.done", { count: destinataires.length }));
    } catch (err) {
      // Le seul refus que l'écran TRADUIT en action : le document est périmé, et
      // l'erreur porte l'identifiant du cahier de charges à refaire.
      if (err instanceof ApiException && err.apiError?.code === "QUOTE_REQUEST_SEND_SPEC_STALE") {
        const specId = (err.apiError.details as { spec_id?: string } | undefined)?.spec_id ?? null;
        setARefaire(specId ?? cahier?.id ?? null);
        setSelectedStep("specification");
      }
      toast.error(messageErreur(err));
    } finally {
      setEnvoiEnCours(false);
    }
  }

  /**
   * Ouvre le document dans un nouvel onglet.
   *
   * L'URL est signée et à durée courte : on la demande au moment du clic plutôt
   * que de la garder en mémoire, où elle périmerait sans prévenir.
   */
  async function ouvrirDocument(fileId: string) {
    try {
      const { url } = await telecharger.mutateAsync(fileId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(messageErreur(err));
    }
  }

  function messageErreur(err: unknown): string {
    return err instanceof ApiException
      ? getApiErrorMessage(err, lang) || t("errors.generic")
      : t("errors.generic");
  }

  // ---------------------------------------------------------------------------
  // États de page
  // ---------------------------------------------------------------------------

  if (demande.isPending) {
    return (
      <PageCadre>
        <div className="space-y-4" aria-busy="true" aria-live="polite">
          <span className="sr-only">{t("prepare_send.loading")}</span>
          <div className="h-8 w-2/3 animate-pulse rounded bg-slate-200 motion-reduce:animate-none" />
          <div className="h-40 animate-pulse rounded-xl bg-slate-100 motion-reduce:animate-none" />
          <div className="h-40 animate-pulse rounded-xl bg-slate-100 motion-reduce:animate-none" />
        </div>
      </PageCadre>
    );
  }

  if (demande.isError || !demande.data) {
    return (
      <PageCadre>
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {t("prepare_send.load_error")}
        </p>
        <button
          type="button"
          onClick={() => void demande.refetch()}
          className="focus-visible:ring-brand-500 mt-3 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
        >
          {t("app.retry")}
        </button>
      </PageCadre>
    );
  }

  // La numérotation suit les étapes RÉELLEMENT rendues. L'effectif s'intercale
  // en 2 quand le dossier en porte un ; sans lui, une suite trouée — 1, 3, 4 —
  // ferait chercher une étape qui n'existe pas.
  // Le suivi garde son rang 4 : il remplace l'envoi une fois celui-ci fait, et
  // le 3 manquant dit justement que cette étape est derrière — comportement
  // d'avant 9.BK.3, décalé mais pas redressé.
  const etatExport = readiness.data?.census_export ?? null;
  const decalage = etatExport ? 1 : 0;
  const invalidExistingSpec =
    readiness.data?.blockers.some((b) => b.code === "QUOTE_REQUEST_SPEC_PURPOSE_INVALID") ?? false;
  const workflow = readiness.data?.workflow;
  const steps: readonly string[] = PREPARATION_SECTIONS;
  // La readiness recommande le prochain travail ; elle autorise l'envoi,
  // pas la navigation. Les mutations de préparation gardent leurs contrôles API.
  const currentStep = selectedStep && steps.includes(selectedStep) ? selectedStep : workflow?.step;
  const effectifAssureur = (
    <EtapeEffectifAssureur
      numero={2}
      etatExport={etatExport}
      modifiable={estBrouillon && !readiness.isError}
      enCours={genererExport.isPending}
      onGenerer={() => void produireExportEffectif()}
      onTelecharger={(fileId) => void ouvrirDocument(fileId)}
    />
  );

  return (
    <PageCadre>
      {sentRequestId === id && demande.data.status !== "cancelled" && (
        <div role="status" className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-4">
          <p className="font-medium">{t("flow_clarity.send_queued")}</p>
          <a
            href="#request-delivery-tracking"
            className="text-brand-700 inline-flex min-h-11 items-center underline"
          >
            {t("flow_clarity.view_tracking")}
          </a>
        </div>
      )}
      <header className="mb-3">
        {presentation === "page" && projectId && (
          <nav
            className="flex flex-wrap items-center gap-x-4"
            aria-label={t("prepare_send.file_navigation")}
          >
            <Link
              to={`/projects/${projectId}?view=preparation&request=${demande.data.id}&request_section=${currentStep ?? "authorization"}`}
              className="focus-visible:ring-brand-500 text-sm text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              ← {t("prepare_send.back_to_project")}
            </Link>
            <Link
              to={`/reponses?project_id=${projectId}`}
              className="focus-visible:ring-brand-500 text-brand-700 inline-flex min-h-11 items-center rounded-md px-2 text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("project_hub.form_responses")}
            </Link>
          </nav>
        )}
        {presentation === "page" && project.data && (
          <p className="mt-3 break-words text-sm text-slate-600">{project.data.title}</p>
        )}
        {presentation === "page" && readiness.data?.delivery?.company_name && (
          <p className="mt-1 break-words font-medium text-slate-800">
            {readiness.data.delivery.company_name}
          </p>
        )}
        {presentation === "page" && (
          <h1 className="mt-2 text-pretty text-2xl font-semibold tracking-tight text-slate-900">
            {t(estBrouillon ? "request_flow.title" : "preparation_context.history_title")}
          </h1>
        )}
        {presentation === "page" && (
          <p className="mt-1 break-words text-sm text-slate-600">
            {demande.data.title || t("prepare_send.untitled")}
          </p>
        )}
      </header>

      <div ref={taskStart} className="scroll-mt-16 lg:scroll-mt-0" />
      {estBrouillon && (
        <nav
          aria-label={t("request_flow.progress")}
          data-project-anchor-occluder
          className="workspace-command-bar mb-4 flex flex-wrap"
        >
          {steps.map((step) => (
            <button
              key={step}
              type="button"
              aria-current={currentStep === step ? "step" : undefined}
              disabled={!workflow}
              onClick={() => setSelectedStep(step)}
              className="aria-[current=step]:border-brand-600 aria-[current=step]:bg-brand-50 focus-visible:ring-brand-500 min-h-11 min-w-0 whitespace-normal break-words border-b-2 border-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40"
            >
              {t(`request_flow.${step}`)}
            </button>
          ))}
        </nav>
      )}
      {readiness.isError && (
        <p role="alert" className="mb-5 text-red-700">
          {t("request_flow.authorization_unavailable")}
        </p>
      )}
      {estBrouillon && !workflow && !readiness.isError && <p role="status">{t("app.loading")}</p>}
      {estBrouillon &&
        workflow &&
        currentStep !== "review" &&
        !readiness.isError &&
        readiness.data?.can_send === false && (
          <SendBlockers presentation="compact" blockers={readiness.data.blockers} />
        )}
      {modificationsNonEnvoyees && (
        <p role="status" className="mb-4 text-sm text-amber-800">
          {t("preparation_context.deadline_unsaved")}
        </p>
      )}
      <div className="space-y-6">
        {estBrouillon && workflow && projectId && (
          <div data-guide-target="authorization" hidden={currentStep !== "authorization"}>
            <AuthorizationStep
              projectId={projectId}
              requestId={demande.data.id}
              onDownload={(fileId) => void ouvrirDocument(fileId)}
            />
          </div>
        )}
        {invalidExistingSpec && (
          <p
            role="alert"
            className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
          >
            {t("request_flow.invalid_existing_spec")}
          </p>
        )}
        {!invalidExistingSpec && (!estBrouillon || !!workflow) && (
          <div hidden={estBrouillon && currentStep !== "specification"} className="space-y-6">
            <div
              data-guide-target="specification"
              aria-busy={cahiers.isPending || reponses.isPending}
              data-guide-unavailable={cahiers.isError || reponses.isError || undefined}
            >
              <EtapeCahierDeCharges
                projectId={projectId}
                cahier={cahier}
                aRefaire={aRefaire !== null}
                chargement={cahiers.isPending || reponses.isPending}
                analyses={(reponses.data?.items ?? []).filter((a) => a.purpose === "business_spec")}
                modifiable={estBrouillon}
                enCours={rattacher.isPending || refaire.isPending}
                demandeId={demande.data.id}
                onChoisir={(rid) => void choisirCahier(rid)}
                onRefaire={() => void refaireDocument()}
                onTelecharger={(fileId) => void ouvrirDocument(fileId)}
              />
            </div>

            {!estBrouillon && effectifAssureur}
            {estBrouillon && projectId && (
              <button
                type="button"
                className="text-brand-700 inline-flex min-h-11 items-center underline"
                onClick={() => setSelectedStep("census")}
              >
                {t("preparation_context.manage_census")}
              </button>
            )}
          </div>
        )}
        {estBrouillon && workflow && projectId && (
          <div hidden={currentStep !== "census"} className="space-y-6">
            <div data-guide-target="census" className="space-y-6">
              {hash !== "#census" && <CensusSection key={projectId} projectId={projectId} />}
              {currentStep === "census" && effectifAssureur}
            </div>
            <button
              type="button"
              className="text-brand-700 min-h-11 underline"
              onClick={() => setSelectedStep("specification")}
            >
              {t("request_flow.specification")}
            </button>
          </div>
        )}
        {estBrouillon && workflow && (
          <div
            data-guide-target="insurers"
            hidden={currentStep !== "insurers"}
            className="space-y-4"
          >
            <EtapeAssureurs
              numero={2 + decalage}
              destinataires={destinataires}
              modifiable={estBrouillon}
              enCours={assureursEnCours}
              onAjouter={(code) => void ajouterUnAssureur(code)}
              onRetirer={(rid) => void retirerUnAssureur(rid)}
              onCorrigerAdresse={(rid, email) => void corrigerUneAdresse(rid, email)}
              onChangerLangue={(rid, language) => void changerLangueDestinataire(rid, language)}
            />

            {currentStep === "insurers" && (
              <>
                <label className="block">
                  <span className="label">{t("prepare_send.send.deadline")}</span>
                  <input
                    className="input"
                    type="date"
                    onInput={(e) => setEcheance(e.currentTarget.value)}
                    value={echeanceEffective}
                    onChange={(e) => setEcheance(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn-primary min-h-11"
                  disabled={!modificationsNonEnvoyees || enregistrerDemande.isPending}
                  onClick={() => void enregistrerEcheance(echeanceEffective)}
                >
                  {t("request_flow.save_deadline")}
                </button>
              </>
            )}
          </div>
        )}
        {estBrouillon && projectId && readiness.data?.delivery && (
          <div hidden={currentStep !== "insurers"}>
            <DeliveryPreparation
              key={demande.data.id}
              requestId={demande.data.id}
              projectId={projectId}
              delivery={readiness.data.delivery}
              recipients={destinataires}
              onDirtyChange={setDeliveryDirty}
              editable={!readiness.isError}
              onDownload={(fileId) => void ouvrirDocument(fileId)}
            />
          </div>
        )}
        {estBrouillon && currentStep === "review" && (
          <div data-guide-target="review">
            <EnvoiRecapitulatif
              censusExport={effectifAssureur}
              numero={3 + decalage}
              readiness={readiness.isError ? undefined : readiness.data}
              readinessRefreshing={readiness.isFetching}
              readinessLoading={readiness.isPending}
              aRefaire={aRefaire !== null}
              destinataires={destinataires}
              echeance={echeanceEffective}
              onEcheanceValidee={(v) => void enregistrerEcheance(v)}
              onEcheance={setEcheance}
              enVol={envoiEnCours}
              uncommittedChanges={
                deliveryDirty || modificationsNonEnvoyees || enregistrerDemande.isPending
              }
              onEnvoyer={() => void envoyerLaDemande()}
            />
          </div>
        )}
        {!estBrouillon && (
          <div
            data-guide-target="followup"
            id="request-delivery-tracking"
            className="scroll-mt-20"
            role="region"
            aria-label={t("guide.view_history")}
          >
            <SuiviParAssureur numero={4 + decalage} destinataires={destinataires} />
          </div>
        )}
        <div className="workspace-action-bar">
          {estBrouillon &&
            workflow &&
            currentStep &&
            steps.indexOf(currentStep) < steps.length - 1 && (
              <button
                type="button"
                className="btn-primary min-h-11"
                onClick={() => setSelectedStep(steps[steps.indexOf(currentStep) + 1])}
              >
                {t("flow.next_section", {
                  section: t(`request_flow.${steps[steps.indexOf(currentStep) + 1]}`),
                })}
              </button>
            )}
          {estBrouillon &&
            workflow &&
            currentStep === "review" &&
            currentStep !== workflow.step && (
              <button
                type="button"
                className="btn-primary min-h-11"
                onClick={() => setSelectedStep(workflow.step)}
              >
                {t("flow.resume_section", { section: t(`request_flow.${workflow.step}`) })}
              </button>
            )}
        </div>
      </div>
    </PageCadre>
  );
}

function PageCadre({ children }: { children: React.ReactNode }) {
  return <div className="w-full min-w-0">{children}</div>;
}
